import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const BudgetSelect = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [minValue, setMinValue] = useState(5);
  const [maxValue, setMaxValue] = useState(5000);
  const dropdownRef = useRef(null);

  const MIN_RANGE = 5;
  const MAX_RANGE = 5000;

  // Initialize from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const min = parseInt(urlParams.get('minPrice')) || MIN_RANGE;
    const max = parseInt(urlParams.get('maxPrice')) || MAX_RANGE;
    
    if (min >= MIN_RANGE && min <= MAX_RANGE) setMinValue(min);
    if (max >= MIN_RANGE && max <= MAX_RANGE && max >= min) setMaxValue(max);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMinChange = (e) => {
    setMinValue(e.target.value);
  };

  const handleMaxChange = (e) => {
    setMaxValue(e.target.value);
  };

  const handleMinBlur = () => {
    const num = parseInt(minValue);
    if (isNaN(num) || num < MIN_RANGE) {
      setMinValue(MIN_RANGE);
    } else if (maxValue !== '' && num > parseInt(maxValue)) {
      setMinValue(maxValue);
    } else {
      setMinValue(num);
    }
  };

  const handleMaxBlur = () => {
    const num = parseInt(maxValue);
    if (isNaN(num) || num > MAX_RANGE) {
      setMaxValue(MAX_RANGE);
    } else if (minValue !== '' && num < parseInt(minValue)) {
      setMaxValue(minValue);
    } else {
      setMaxValue(num);
    }
  };

  const handleMinFocus = (e) => {
    e.target.select();
  };

  const handleMaxFocus = (e) => {
    e.target.select();
  };

  const clearAll = () => {
    setMinValue(MIN_RANGE);
    setMaxValue(MAX_RANGE);
    const url = new URL(window.location);
    url.searchParams.delete('minPrice');
    url.searchParams.delete('maxPrice');
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  const applyFilters = () => {
    const url = new URL(window.location);
    const minNum = parseInt(minValue) || MIN_RANGE;
    const maxNum = parseInt(maxValue) || MAX_RANGE;

    if (minNum > MIN_RANGE) {
      url.searchParams.set('minPrice', minNum);
    } else {
      url.searchParams.delete('minPrice');
    }
    
    if (maxNum < MAX_RANGE) {
      url.searchParams.set('maxPrice', maxNum);
    } else {
      url.searchParams.delete('maxPrice');
    }
    
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block w-[80px] md:w-[110px]" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full px-2 md:px-4 py-1 md:py-2 text-[12px] md:text-sm font-medium text-gray-700 bg-white border border-[#185BC3] rounded-[50px] shadow-sm hover:bg-gray-50 focus:outline-none"
      >
        <span className='text-[#185BC3]'>Budget</span>
        <ChevronDown className={`w-4 h-4 ml-0 transition-transform text-[#185BC3] font-extrabold duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="p-4 absolute z-10 w-64 mt-2 origin-top-right bg-white border border-[#9D9D9D40] rounded-md shadow-lg">
          <div className="flex flex-col gap-4">
            <div>
              <p className='text-black font-medium font-poppins text-[16px]'>Select Budget</p>
            </div>

            {/* Input fields */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="number"
                  value={minValue}
                  onChange={handleMinChange}
                  onBlur={handleMinBlur}
                  onFocus={handleMinFocus}
                  className="w-full px-3 py-2 text-center border border-gray-300 rounded-[20px] focus:outline-none focus:border-[#174568]"
                  placeholder="Min"
                />
                <label className="block text-center text-xs text-gray-500 mt-1">Min.</label>
              </div>
              
              <div className="flex-1">
                <input
                  type="number"
                  value={maxValue}
                  onChange={handleMaxChange}
                  onBlur={handleMaxBlur}
                  onFocus={handleMaxFocus}
                  className="w-full px-3 py-2 text-center border border-gray-300 rounded-[20px] focus:outline-none focus:border-[#174568]"
                  placeholder="Max"
                />
                <label className="block text-center text-xs text-gray-500 mt-1">Max.</label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-3">
              <button 
                onClick={clearAll}
                className='border-[1px] border-[#174568] text-[#174568] text-[15px] rounded-[30px] py-[.5px] px-3 hover:bg-gray-50'
              >
                Clear
              </button>
              <button 
                onClick={applyFilters}
                className='border-[1px] bg-[#174568] border-[#174568] text-[15px] text-white rounded-[30px] py-[.5px] px-3 hover:bg-[#0f3a4f]'
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetSelect;